// src/components/UpdateNotification.tsx
import { useEffect, useState, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    LinearProgress,
    Box,
    Button,
    DialogActions
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const COUNTDOWN_SECONDS = 3;

const UpdateNotification = () => {
    const [open, setOpen] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    const countdownIntervalRef = useRef<number | null>(null);
    const reloadTimeoutRef = useRef<number | null>(null);
    const hasTriggeredUpdateRef = useRef(false);
    const controllerChangeHandlerRef = useRef<(() => void) | null>(null);

    const clearCountdownInterval = useCallback(() => {
        if (countdownIntervalRef.current !== null) {
            window.clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    }, []);

    const clearReloadTimeout = useCallback(() => {
        if (reloadTimeoutRef.current !== null) {
            window.clearTimeout(reloadTimeoutRef.current);
            reloadTimeoutRef.current = null;
        }
    }, []);

    const detachControllerChangeListener = useCallback(() => {
        const handler = controllerChangeHandlerRef.current;
        if (handler && navigator.serviceWorker?.removeEventListener) {
            navigator.serviceWorker.removeEventListener('controllerchange', handler);
        }
        controllerChangeHandlerRef.current = null;
    }, []);

    const runUpdate = useCallback(() => {
        if (hasTriggeredUpdateRef.current) return;
        hasTriggeredUpdateRef.current = true;

        clearCountdownInterval();

        navigator.serviceWorker?.controller?.postMessage({ type: 'SKIP_WAITING' });

        const reload = () => window.location.reload();
        if (navigator.serviceWorker?.addEventListener) {
            controllerChangeHandlerRef.current = reload;
            navigator.serviceWorker.addEventListener('controllerchange', reload);
        }

        clearReloadTimeout();
        reloadTimeoutRef.current = window.setTimeout(() => {
            reload();
            detachControllerChangeListener();
        }, 5000);
    }, [clearCountdownInterval, clearReloadTimeout, detachControllerChangeListener]);

    const startCountdown = useCallback(() => {
        clearCountdownInterval();

        countdownIntervalRef.current = window.setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearCountdownInterval();
                    runUpdate();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [clearCountdownInterval, runUpdate]);

    useEffect(() => {
        const handler = () => setOpen(true);
        window.addEventListener('pwa-update', handler);
        return () => window.removeEventListener('pwa-update', handler);
    }, []);

    useEffect(() => {
        if (!open) {
            clearCountdownInterval();
            clearReloadTimeout();
            detachControllerChangeListener();
            hasTriggeredUpdateRef.current = false;
            setCountdown(COUNTDOWN_SECONDS);
            return;
        }

        setCountdown(COUNTDOWN_SECONDS);
        startCountdown();

        return () => {
            clearCountdownInterval();
        };
    }, [open, clearCountdownInterval, clearReloadTimeout, startCountdown, detachControllerChangeListener]);

    useEffect(() => () => {
        clearCountdownInterval();
        clearReloadTimeout();
        detachControllerChangeListener();
    }, [clearCountdownInterval, clearReloadTimeout, detachControllerChangeListener]);

    const handleUpdate = () => {
        runUpdate();
    };

    return (
        <Dialog
            fullScreen
            open={open}
            PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.9)', color: '#fff' } }}
        >
            <DialogContent>
                <Box
                    sx={{
                        height: '60vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        px: 2
                    }}
                >
                    <SystemUpdateAltIcon sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />
                    <Typography variant="h5" gutterBottom align="center" fontWeight="bold">
                        新しいバージョンが利用可能です
                    </Typography>
                    <Typography variant="body1" align="center" sx={{ color: '#aaa', mb: 4 }}>
                        入力中の内容は自動的に保存されます
                    </Typography>
                    <Typography variant="h2" sx={{ my: 2, fontWeight: 'bold', color: 'primary.main' }}>
                        {countdown}
                    </Typography>
                    <Typography variant="body1" align="center">
                        {countdown}秒後に自動的に更新されます
                    </Typography>
                    <Box sx={{ width: '100%', mt: 4, maxWidth: 400 }}>
                        <LinearProgress
                            variant="determinate"
                            value={((COUNTDOWN_SECONDS - countdown) / COUNTDOWN_SECONDS) * 100}
                            color="primary"
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleUpdate}
                    sx={{ px: 6, py: 1.5, fontSize: '1.1rem' }}
                >
                    今すぐ更新
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateNotification;
